import { useContext, useState } from 'react';
import styled from 'styled-components';

import AnalyticsProvider from 'contexts/analytics';
import { useCapabilities } from 'contexts/capabilities';
import { SessionContext } from 'contexts/session';
import EnterpriseUpsell, {
  ANALYTICS_UPSELL_COPY,
  ENTERPRISE_UPSELL_FEATURES,
  ENTERPRISE_UPSELL_METRICS,
  HighlightedText,
} from 'components/enterpriseUpsell';

import AnalyticsContent from './components/AnalyticsContent/AnalyticsContent';
import AnalyticsEmpty from './components/AnalyticsEmpty';

import { topBarHeight as tbh } from 'utils/helpers';
import { screenMd } from 'constants/styles/breakpoints';

const PageContent = styled.div<{ translateY?: string; showingNotification?: boolean }>`
  background: var(--ghost-white);

  ${({ translateY }) =>
    translateY &&
    `
    padding-top: ${translateY};
  `}

  ${({ showingNotification, translateY }) =>
    showingNotification &&
    `
    ${screenMd} {
      padding-top: calc( ${translateY} + 40px);
    }
  `}
`;

const Analytics = (): JSX.Element => {
  const { isAccountOwner, account, user, notShowingDrawers } = useContext(SessionContext);
  const { canViewAnalytics } = useCapabilities();
  const amountOfUsers = account?.users_count ? Number(account.users_count) : 0;

  // 130 stands for the height of the filters (64) plus the height of Analytics title (56) + padding (10)
  const basicHeight = tbh(user) + 130;

  const [showingNotification, setShowingNotification] = useState(false);

  const onCloseNotification = () => {
    setShowingNotification(false);
  };

  return (
    <>
      {!canViewAnalytics ? (
        <PageContent translateY={notShowingDrawers && `${tbh(user)}px`}>
          <EnterpriseUpsell
            eyebrow={ANALYTICS_UPSELL_COPY.eyebrow}
            headline={
              <>
                Unlock <HighlightedText>{ANALYTICS_UPSELL_COPY.headlineHighlight}</HighlightedText>{' '}
                with Enterprise
              </>
            }
            description={ANALYTICS_UPSELL_COPY.description}
            features={ENTERPRISE_UPSELL_FEATURES}
            metrics={ENTERPRISE_UPSELL_METRICS}
          />
        </PageContent>
      ) : isAccountOwner && amountOfUsers === 0 ? (
        <PageContent
          translateY={notShowingDrawers && `${basicHeight}px`}
          showingNotification={showingNotification}
        >
          <AnalyticsEmpty />
        </PageContent>
      ) : (
        <AnalyticsProvider>
          <PageContent
            translateY={notShowingDrawers && `${basicHeight}px`}
            showingNotification={showingNotification}
          >
            <AnalyticsContent
              onCloseNotification={onCloseNotification}
              showingNotification={showingNotification}
            />
          </PageContent>
        </AnalyticsProvider>
      )}
    </>
  );
};

export default Analytics;
